// The Token classes breakdown (SCR-584, REQ-233): a line's five classes — input, output, cache
// read, cache write (5 min) and cache write (1 h) — with their tokens and, as the server allowed,
// their AWS list cost and price. Pure and DOM-free (tests/unit/usage/classes-model.test.ts).
//
// Money is never computed here (U4): a class's figure is the server's split, and a money column
// exists only when the server sent its split as an object and the viewer's policy shows it. The
// total row repeats the line's own figures, which the five classes sum to on the server.
import { applyMoneyPolicy, type MoneyPolicy } from "../../api/org-context";
import { classRows, totalTokens, type ClassLine, type UsageTokenClass } from "./figures";

/** How each class reads in a table, in the order the rows run. */
export const USAGE_CLASS_LABELS: Readonly<Record<UsageTokenClass, string>> = {
  input: "Input",
  output: "Output",
  cacheRead: "Cache read",
  cacheWrite5m: "Cache write (5 min)",
  cacheWrite1h: "Cache write (1 h)",
};

export interface ClassTableRow {
  key: UsageTokenClass | "total";
  label: string;
  tokens: number;
  cost: number | null;
  price: number | null;
}

export interface ClassTable {
  rows: ClassTableRow[];
  total: ClassTableRow;
  /** The money columns that exist: one only when the server split that figure (it is an object). */
  showCost: boolean;
  showPrice: boolean;
  /** The models the line's money leaves out (REQ-017), for the partial-money note; empty when whole. */
  unpricedModels: readonly string[];
  /** No tokens of any class: the panel says so instead of a table of zeros. */
  empty: boolean;
}

/**
 * The line as the viewer may see it: a figure the policy hides is null, and so is its split, so a
 * hidden figure's columns disappear with it. Never fills in one the server left null.
 */
export function classesUnderPolicy(policy: MoneyPolicy, line: ClassLine): ClassLine {
  return {
    ...applyMoneyPolicy(policy, line),
    costByClassMicroUsd: policy.list ? line.costByClassMicroUsd : null,
    priceByClassMicroUsd: policy.price ? line.priceByClassMicroUsd : null,
  };
}

/** The five rows and the total of a line's Token classes table. */
export function classTable(line: ClassLine): ClassTable {
  const rows = classRows(line).map((row) => ({ key: row.class, label: USAGE_CLASS_LABELS[row.class], tokens: row.tokens, cost: row.cost, price: row.price }));
  const showCost = line.costByClassMicroUsd !== null;
  const showPrice = line.priceByClassMicroUsd !== null;
  const tokens = totalTokens(line);
  return {
    rows,
    total: { key: "total", label: "Total", tokens, cost: showCost ? line.costMicroUsd : null, price: showPrice ? line.priceMicroUsd : null },
    showCost,
    showPrice,
    unpricedModels: line.unpricedModels,
    empty: tokens === 0,
  };
}
